/**
 * TUVI - Informe de avance semanal por capítulos (Fase 3).
 * Arma el informe (No., fechas, % por capítulo, observaciones), guarda un "corte"
 * (para el "% semana anterior" del próximo) y genera un PDF listo para el cliente
 * con registro fotográfico embebido.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import { UPLOAD_URL } from '../../../config';
import './ModalInformeAvance.css';

const hoyISO = () => new Date().toISOString().split('T')[0];
const fmtFecha = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const fotoUrl = (ruta) => `${UPLOAD_URL}/catalogo/img/evidencia-cronograma/${ruta}`;

async function imgABase64(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((r) => { const fr = new FileReader(); fr.onloadend = () => r(fr.result); fr.onerror = () => r(null); fr.readAsDataURL(blob); });
  } catch { return null; }
}

const ModalInformeAvance = ({ show, onClose, proyectoId, onGenerado }) => {
  const { success, error } = useNotification();
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [form, setForm] = useState({ numero: 1, fecha_corte: hoyISO(), fecha_entrega: '', observaciones_generales: '' });
  const [caps, setCaps] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(`query($id: Int!) {
        datosInformeAvance(proyecto_id: $id) {
          proyecto_nombre apartamento nombre_cliente fecha_estimada_entrega siguiente_numero pct_programacion_sugerido
          capitulos { capitulo_nombre orden pct_a_la_fecha pct_programacion pct_semana_anterior }
          fotos { id ruta descripcion semana }
        }
      }`, { id: parseInt(proyectoId) });
      const r = d.datosInformeAvance;
      setDatos(r);
      setForm({ numero: r.siguiente_numero || 1, fecha_corte: hoyISO(), fecha_entrega: r.fecha_estimada_entrega || '', observaciones_generales: '' });
      setCaps((r.capitulos || []).map(c => ({
        capitulo_nombre: c.capitulo_nombre, orden: c.orden || 0,
        pct_semana_anterior: c.pct_semana_anterior || 0,
        pct_a_la_fecha: c.pct_a_la_fecha || 0,
        pct_programacion: c.pct_programacion != null ? c.pct_programacion : (r.pct_programacion_sugerido || 0),
        observaciones: '',
      })));
    } catch (e) { error('Error al cargar los datos del informe'); }
    finally { setLoading(false); }
  }, [proyectoId, error]);

  useEffect(() => { if (show) cargar(); }, [show, cargar]);

  const setCap = (i, campo, val) => setCaps(prev => prev.map((c, j) => j === i ? { ...c, [campo]: val } : c));
  const desv = (c) => (parseFloat(c.pct_a_la_fecha) || 0) - (parseFloat(c.pct_programacion) || 0);

  const generar = async () => {
    if (!form.numero || !form.fecha_corte) { error('Indica el número y la fecha de corte'); return; }
    setGenerando(true);
    try {
      // 1. Guardar el corte (snapshot) para el próximo informe
      await client.mutate(`mutation($input: InformeAvanceInput!) { generarInformeAvance(input: $input) { success message id } }`, {
        input: {
          proyecto_id: parseInt(proyectoId),
          numero_informe: parseInt(form.numero),
          fecha_corte: form.fecha_corte,
          fecha_estimada_entrega: form.fecha_entrega || null,
          observaciones_generales: form.observaciones_generales || null,
          capitulos: caps.map(c => ({
            capitulo_nombre: c.capitulo_nombre, orden: c.orden,
            pct_a_la_fecha: parseFloat(c.pct_a_la_fecha) || 0,
            pct_programacion: parseFloat(c.pct_programacion) || 0,
            observaciones: c.observaciones || null,
          })),
        },
      });

      // 2. Preparar fotos en base64
      const fotos = datos?.fotos || [];
      const fotosB64 = [];
      for (const f of fotos) {
        const b64 = await imgABase64(fotoUrl(f.ruta));
        if (b64) fotosB64.push({ ...f, b64 });
      }

      // 2b. Logo del sistema principal (mismo /logo.png del sidebar) en base64
      const logoB64 = await imgABase64(`${window.location.origin}/logo.png`);

      // 3. Construir el HTML del informe
      const filasCap = caps.map(c => `
        <tr>
          <td style="text-align:left;font-weight:600">${c.capitulo_nombre}</td>
          <td>${(parseFloat(c.pct_semana_anterior) || 0).toFixed(0)}%</td>
          <td>${(parseFloat(c.pct_a_la_fecha) || 0).toFixed(0)}%</td>
          <td>${(parseFloat(c.pct_programacion) || 0).toFixed(0)}%</td>
          <td style="color:${desv(c) < 0 ? '#dc2626' : '#15803d'}">${desv(c).toFixed(0)}%</td>
          <td style="text-align:left;font-size:9px">${(c.observaciones || '').replace(/</g, '&lt;')}</td>
        </tr>`).join('');

      const fotosHtml = fotosB64.length > 0 ? `
        <div class="reg-foto-titulo">REGISTRO FOTOGRÁFICO</div>
        <div class="fotos-grid">
          ${fotosB64.map(f => `
            <div class="foto-item">
              <img src="${f.b64}" />
              <div class="foto-cap">${f.semana || ''}${f.descripcion ? ' — ' + f.descripcion.replace(/</g, '&lt;') : ''}</div>
            </div>`).join('')}
        </div>` : '';

      const html = `
        <div class="inf-page">
          <div class="inf-head">
            ${logoB64 ? `<img class="inf-logo-img" src="${logoB64}" alt="TUVI" />` : `<div class="inf-logo">TUVI</div>`}
          </div>
          <div class="inf-linea"></div>
          <table class="inf-meta">
            <tr><td class="lbl">PROYECTO</td><td>${datos?.proyecto_nombre || ''}${datos?.apartamento ? ' - Apto ' + datos.apartamento : ''}</td></tr>
            <tr><td class="lbl">CLIENTE</td><td>${datos?.nombre_cliente || ''}</td></tr>
            <tr><td class="lbl">INFORME DE AVANCE SEMANAL No.</td><td>${form.numero}</td></tr>
            <tr><td class="lbl">FECHA DE CORTE</td><td>${fmtFecha(form.fecha_corte)}</td></tr>
            <tr><td class="lbl">FECHA ESTIMADA DE ENTREGA</td><td>${fmtFecha(form.fecha_entrega)}</td></tr>
          </table>
          <table class="inf-tabla">
            <thead>
              <tr>
                <th style="width:26%">SEMANA / ACTIVIDAD</th>
                <th>% EJECUTADO SEMANA ANTERIOR</th>
                <th>% EJECUTADO A LA FECHA</th>
                <th>% AVANCE SEGÚN PROGRAMACIÓN</th>
                <th>% DESVIACIÓN</th>
                <th style="width:26%">OBSERVACIONES</th>
              </tr>
            </thead>
            <tbody>${filasCap}</tbody>
          </table>
          ${form.observaciones_generales ? `<div class="inf-obs"><strong>Observaciones generales:</strong> ${form.observaciones_generales.replace(/</g, '&lt;')}</div>` : ''}
          ${fotosHtml}
        </div>`;

      const cont = document.createElement('div');
      cont.style.position = 'fixed'; cont.style.left = '-9999px'; cont.style.top = '0';
      cont.innerHTML = `<style>${ESTILOS_PDF}</style>${html}`;
      document.body.appendChild(cont);

      const html2pdf = (await import('html2pdf.js')).default;
      const nombreArch = `Informe_Avance_${(datos?.proyecto_nombre || 'Proyecto').replace(/[^a-zA-Z0-9]+/g, '_')}_No${form.numero}.pdf`;
      await html2pdf().set({
        margin: [8, 8, 8, 8], filename: nombreArch,
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(cont.querySelector('.inf-page')).save();

      document.body.removeChild(cont);
      success(`Informe No. ${form.numero} generado`);
      onGenerado && onGenerado();
      onClose();
    } catch (e) { console.error(e); error('Error al generar el informe'); }
    finally { setGenerando(false); }
  };

  if (!show) return null;

  return (
    <div className="ia-overlay" onClick={onClose}>
      <div className="ia-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ia-header">
          <h3>📄 Generar Informe de Avance Semanal</h3>
          <button className="ia-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="ia-empty">Cargando datos...</div>
        ) : (
          <div className="ia-body">
            <div className="ia-form-row">
              <div className="ia-field"><label>No. de informe</label><input type="number" min="1" value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} /></div>
              <div className="ia-field"><label>Fecha de corte</label><input type="date" value={form.fecha_corte} onChange={e => setForm({ ...form, fecha_corte: e.target.value })} /></div>
              <div className="ia-field"><label>Fecha estimada de entrega</label><input type="date" value={form.fecha_entrega} onChange={e => setForm({ ...form, fecha_entrega: e.target.value })} /></div>
            </div>

            <div className="ia-tabla-wrap">
              <table className="ia-tabla">
                <thead>
                  <tr>
                    <th>Semana / Actividad</th>
                    <th>% Sem. anterior</th>
                    <th>% A la fecha</th>
                    <th>% Programación</th>
                    <th>% Desviación</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {caps.map((c, i) => (
                    <tr key={c.capitulo_nombre + i}>
                      <td className="ia-cap-nombre">{c.capitulo_nombre}</td>
                      <td className="ia-ro">{(parseFloat(c.pct_semana_anterior) || 0).toFixed(0)}%</td>
                      <td><input type="number" min="0" max="100" value={c.pct_a_la_fecha} onChange={e => setCap(i, 'pct_a_la_fecha', e.target.value)} /></td>
                      <td><input type="number" min="0" max="100" value={c.pct_programacion} onChange={e => setCap(i, 'pct_programacion', e.target.value)} /></td>
                      <td className="ia-ro" style={{ color: desv(c) < 0 ? '#dc2626' : '#15803d' }}>{desv(c).toFixed(0)}%</td>
                      <td><input type="text" value={c.observaciones} onChange={e => setCap(i, 'observaciones', e.target.value)} placeholder="—" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="ia-hint">El informe sigue las <strong>semanas del cronograma de este proyecto</strong>, así que cambia con él. El "% a la fecha" es el avance de cada semana según su check list; el "% programación" sale de las fechas planeadas de esa semana. Ambos son editables. El "% semana anterior" es del último informe generado.</p>
            </div>

            <div className="ia-field">
              <label>Observaciones generales</label>
              <textarea rows="2" value={form.observaciones_generales} onChange={e => setForm({ ...form, observaciones_generales: e.target.value })} placeholder="Comentario general para el cliente..." />
            </div>

            <div className="ia-fotos">
              <strong>Registro fotográfico ({datos?.fotos?.length || 0})</strong>
              {(datos?.fotos?.length || 0) === 0
                ? <p className="ia-hint">Sin fotos. Sube evidencias tipo foto en las semanas del cronograma para que aparezcan aquí.</p>
                : <div className="ia-fotos-grid">{datos.fotos.map(f => <img key={f.id} src={fotoUrl(f.ruta)} alt={f.descripcion || ''} title={`${f.semana || ''}${f.descripcion ? ' — ' + f.descripcion : ''}`} />)}</div>}
            </div>
          </div>
        )}

        <div className="ia-footer">
          <button className="ia-btn-cancel" onClick={onClose} disabled={generando}>Cancelar</button>
          <button className="ia-btn-gen" onClick={generar} disabled={generando || loading}>{generando ? 'Generando...' : '📄 Generar PDF'}</button>
        </div>
      </div>
    </div>
  );
};

const ESTILOS_PDF = `
  .inf-page { width: 194mm; font-family: Arial, sans-serif; color: #1a1a1a; padding: 4mm; }
  .inf-head { display: flex; align-items: center; }
  .inf-logo-img { height: 42px; width: auto; }
  .inf-logo { font-size: 22px; font-weight: 800; color: #3f6212; }
  .inf-linea { height: 4px; background: #AFDC7E; margin: 6px 0 10px; }
  .inf-meta { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 10px; }
  .inf-meta td { padding: 3px 6px; border: 1px solid #e2e8f0; }
  .inf-meta .lbl { font-weight: 700; background: #f1f5f9; width: 38%; }
  .inf-tabla { width: 100%; border-collapse: collapse; font-size: 9px; }
  .inf-tabla th { background: #AFDC7E; color: #1a1a1a; border: 1px solid #9bbf6a; padding: 5px 4px; text-align: center; font-size: 8px; }
  .inf-tabla td { border: 1px solid #cbd5e1; padding: 5px 4px; text-align: center; }
  .inf-obs { margin-top: 10px; font-size: 10px; border: 1px solid #e2e8f0; padding: 8px; border-radius: 4px; }
  .reg-foto-titulo { margin-top: 16px; background: #AFDC7E; color: #1a1a1a; font-weight: 700; padding: 6px 10px; font-size: 11px; border-radius: 4px; }
  .fotos-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px; }
  .foto-item { break-inside: avoid; }
  .foto-item img { width: 100%; height: 62mm; object-fit: cover; border: 1px solid #cbd5e1; border-radius: 4px; }
  .foto-cap { font-size: 8px; color: #475569; margin-top: 3px; }
`;

export default ModalInformeAvance;
